import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import AdminLink from '../components/AdminLink';
import { apiFetch } from '../jscontrol/apiClient';
import { useSettings } from '../../context/SettingsContext';
import '../css/Category.css';

const BundleProductPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { formatPrice } = useSettings();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  const [filterText, setFilterText] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortKey, setSortKey] = useState('ProductName');
  const [sortDir, setSortDir] = useState('asc');
  const [page, setPage] = useState(1);
  const [PAGE_SIZE, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const hasDropdownFilters = !!statusFilter;

  const matchesStatusFilter = (value, filter) => {
    if (!filter) return true;
    const active = value === true || value === 1 || String(value).toLowerCase() === 'true';
    if (filter === 'active') return active;
    if (filter === 'inactive') return !active;
    return true;
  };

  const applyProductFilters = (items) =>
    items.filter((p) => matchesStatusFilter(p.IsActive, statusFilter));

  const showToast = (message, type = 'danger') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  useEffect(() => {
    if (location.state?.toast) {
      showToast(location.state.toast, 'success');
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchBundleProducts = async (currentPage, pageSize, search, sk, sd) => {
    setLoading(true);
    setError(null);
    try {
      const useClientPaging = hasDropdownFilters;
      const params = new URLSearchParams({
        pageIndex: useClientPaging ? '1' : String(currentPage),
        pageSize: useClientPaging ? '500' : String(pageSize),
        search: search || '',
        sortColumn: sk,
        sortBy: sd.toUpperCase(),
      });
      if (statusFilter === 'active') params.set('IsActive', 'true');
      if (statusFilter === 'inactive') params.set('IsActive', 'false');

      const { data } = await apiFetch(`/BundleProduct/GetAll?${params}`);
      let items = Array.isArray(data?.data) ? data.data : [];
      items = applyProductFilters(items);

      if (useClientPaging) {
        const start = (currentPage - 1) * pageSize;
        setProducts(items.slice(start, start + pageSize));
        setTotalCount(items.length);
      } else {
        setProducts(items);
        setTotalCount(data?.totalRecords ?? data?.totalCount ?? items.length);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBundleProducts(page, PAGE_SIZE, filterText, sortKey, sortDir);
  }, [page, PAGE_SIZE, filterText, sortKey, sortDir, statusFilter]);

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this bundle product?')) return;
    try {
      await apiFetch(`/BundleProduct/Delete/${id}`, { method: 'DELETE' });
      showToast('Bundle product deleted successfully.', 'success');
      fetchBundleProducts(page, PAGE_SIZE, filterText, sortKey, sortDir);
    } catch (e) {
      showToast(e.message);
    }
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const goToPage = (p) => setPage(Math.max(1, Math.min(p, totalPages)));
  const handleFilter = (val) => { setFilterText(val); setPage(1); };
  const handleDropdownFilter = (setter) => (e) => { setter(e.target.value); setPage(1); };
  const handlePageSize = (val) => { setPageSize(Number(val)); setPage(1); };

  const handleSortCol = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
    setPage(1);
  };

  const SortIcon = ({ col }) => {
    if (sortKey !== col) return <i className="mdi mdi-sort ms-1" style={{ opacity: 0.3 }}></i>;
    return sortDir === 'asc'
      ? <i className="mdi mdi-sort-ascending ms-1" style={{ color: '#6f42c1' }}></i>
      : <i className="mdi mdi-sort-descending ms-1" style={{ color: '#6f42c1' }}></i>;
  };

  const renderPages = () => {
    const pages = [];
    const delta = 2;
    const left = Math.max(2, safePage - delta);
    const right = Math.min(totalPages - 1, safePage + delta);
    pages.push(<button key={1} className={`cat-page-btn${safePage === 1 ? ' active' : ''}`} onClick={() => goToPage(1)}>1</button>);
    if (left > 2) pages.push(<span key="l-ellipsis" className="cat-page-ellipsis">…</span>);
    for (let i = left; i <= right; i++) {
      pages.push(<button key={i} className={`cat-page-btn${safePage === i ? ' active' : ''}`} onClick={() => goToPage(i)}>{i}</button>);
    }
    if (right < totalPages - 1) pages.push(<span key="r-ellipsis" className="cat-page-ellipsis">…</span>);
    if (totalPages > 1) pages.push(<button key={totalPages} className={`cat-page-btn${safePage === totalPages ? ' active' : ''}`} onClick={() => goToPage(totalPages)}>{totalPages}</button>);
    return pages;
  };

  return (
    <AdminLayout>
      <p style={{ margin: '0 0 12px 0', fontSize: '0.85rem', color: '#6c757d' }}>
        <AdminLink to="/admin">Dashboard</AdminLink>
        <span style={{ margin: '0 6px' }}>/</span>
        <span>Bundle Products</span>
      </p>

      <div className="row">
        <div className="col-lg-12 grid-margin stretch-card">
          <div className="card">
            <div className="card-body">
              {toast && (
                <div className={`alert alert-${toast.type} alert-dismissible d-flex align-items-center py-2 mb-3`} role="alert" style={{ fontSize: '0.875rem' }}>
                  <i className={`mdi ${toast.type === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle'} me-2`} style={{ fontSize: '1.1rem' }}></i>
                  <span>{toast.message}</span>
                  <button type="button" className="btn-close ms-auto" style={{ fontSize: '0.7rem' }} onClick={() => setToast(null)} aria-label="Close" />
                </div>
              )}

              <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                <h4 className="card-title mb-0">Bundle Products</h4>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <div className="cat-filter-wrap">
                    <i className="mdi mdi-magnify cat-filter-icon"></i>
                    <input
                      type="text"
                      className="cat-filter-input"
                      placeholder="Search bundle product..."
                      value={filterText}
                      onChange={e => handleFilter(e.target.value)}
                    />
                  </div>
                  <button className="cat-add-btn" onClick={() => navigate('/admin/bundleproduct/create')}>
                    <i className="mdi mdi-plus" style={{ marginRight: 6 }}></i> Add Bundle Product
                  </button>
                </div>
              </div>

              <div className="d-flex flex-wrap gap-2 mb-3 align-items-end">
                <div>
                  <label className="cat-filter-label">Status</label>
                  <select
                    className="cat-filter-select"
                    value={statusFilter}
                    onChange={handleDropdownFilter(setStatusFilter)}
                  >
                    <option value="">All</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>

              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : error ? (
                <div className="alert alert-danger d-flex align-items-center py-2" style={{ fontSize: '0.875rem' }}>
                  <i className="mdi mdi-alert-circle me-2" style={{ fontSize: '1.1rem' }}></i>
                  {error}
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-striped mb-0">
                    <thead>
                      <tr>
                        <th className="cat-sort-th" onClick={() => handleSortCol('ProductName')}>
                          Product Name <SortIcon col="ProductName" />
                        </th>
                        <th className="cat-sort-th d-none d-sm-table-cell" onClick={() => handleSortCol('CategoryName')}>
                          Category <SortIcon col="CategoryName" />
                        </th>
                        <th className="cat-sort-th d-none d-lg-table-cell" onClick={() => handleSortCol('SKU')}>
                          SKU <SortIcon col="SKU" />
                        </th>
                        <th className="cat-sort-th d-none d-lg-table-cell" onClick={() => handleSortCol('Price')}>Price <SortIcon col="Price" /></th>
                        <th className="cat-sort-th d-none d-xl-table-cell" onClick={() => handleSortCol('Gender')}>Gender <SortIcon col="Gender" /></th>
                        <th className="d-none d-md-table-cell">Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {products.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="text-center py-4 text-muted">No bundle products found.</td>
                        </tr>
                      ) : products.map((p) => (
                        <tr key={p.BundleProductId}>
                          <td className="cat-name-cell">{p.ProductName}</td>
                          <td className="d-none d-sm-table-cell">{p.CategoryName || '—'}</td>
                          <td className="d-none d-lg-table-cell">{p.SKU || '—'}</td>
                          <td className="d-none d-lg-table-cell">{p.Price != null ? formatPrice(p.Price) : '—'}</td>
                          <td className="d-none d-xl-table-cell">{p.Gender || '—'}</td>
                          <td className="d-none d-md-table-cell">
                            <label className={`badge ${p.IsActive ? 'badge-success' : 'badge-danger'}`}>
                              {p.IsActive ? 'Active' : 'Inactive'}
                            </label>
                          </td>
                          <td>
                            <button
                              className="btn btn-sm btn-outline-primary me-1"
                              style={{ border: 'none' }}
                              onClick={() => navigate(`/admin/bundleproduct/edit/${p.BundleProductId}`)}
                              title="Edit"
                            >
                              <i className="mdi mdi-pencil"></i>
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              style={{ border: 'none' }}
                              onClick={() => handleDelete(p.BundleProductId)}
                              title="Delete"
                            >
                              <i className="mdi mdi-delete"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {!loading && !error && totalCount > 0 && (
                <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
                  <div className="d-flex align-items-center gap-2">
                    <span className="cat-page-info">Rows per page:</span>
                    <select className="cat-pagesize-select" value={PAGE_SIZE} onChange={e => handlePageSize(e.target.value)}>
                      <option value={10}>10</option>
                      <option value={30}>30</option>
                      <option value={50}>50</option>
                    </select>
                    <span className="cat-page-info">
                      Showing {(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, totalCount)} of {totalCount}
                    </span>
                  </div>
                  <div className="cat-pagination">
                    <button className="cat-page-btn" onClick={() => goToPage(1)} disabled={safePage === 1} title="First"><i className="mdi mdi-chevron-double-left"></i></button>
                    <button className="cat-page-btn" onClick={() => goToPage(safePage - 1)} disabled={safePage === 1} title="Previous"><i className="mdi mdi-chevron-left"></i></button>
                    {renderPages()}
                    <button className="cat-page-btn" onClick={() => goToPage(safePage + 1)} disabled={safePage === totalPages} title="Next"><i className="mdi mdi-chevron-right"></i></button>
                    <button className="cat-page-btn" onClick={() => goToPage(totalPages)} disabled={safePage === totalPages} title="Last"><i className="mdi mdi-chevron-double-right"></i></button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default BundleProductPage;
